import React, { useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import RunPanel from "./RunPanel";
import RunPicker from "./RunPicker";
import { zb, inr, fileUrl } from "./api";
import styles from "./ZohoBilling.module.css";

const COPY = {
  credits: {
    title: "1. Apply unused vendor credits",
    what: "Applies Ingram's unused vendor credits (from credit notes) to outstanding Ingram bills, oldest first. Partial amounts spill into the next bill.",
    from: "Credit",
    planFile: "credit_merge_plan.json",
  },
  payments: {
    title: "2. Apply advance payments",
    what: "Applies Ingram's unapplied advance payments to outstanding Ingram bills, oldest first. Run it after the credits so it works from the reduced balances.",
    from: "Payment #",
    planFile: "payment_merge_plan.json",
  },
};

function PlanBody({ run, kind }) {
  const c = COPY[kind];
  const p = run.preview;
  const r = run.result;
  if (!p) return null;
  return (
    <>
      {p.headline && <div className={styles.headline}>{p.headline}</div>}
      <div className={styles.sectionLabel}>
        {r ? "Applied" : "Plan — nothing applied yet"}: {p.count} application(s), {inr(p.total)}
      </div>
      {r && (
        <div className={r.failed.length ? styles.warnBox : styles.okBox}>
          {r.applied} applied in Zoho{r.failed.length ? `, ${r.failed.length} failed:` : "."}
          {r.failed.map((l) => (
            <div key={l} className={styles.small}>
              {l}
            </div>
          ))}
        </div>
      )}
      {p.count > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{c.from}</th>
                <th>Bill</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {p.applications.slice(0, 500).map((a, i) => (
                <tr key={`${a.from}-${a.bill}-${i}`}>
                  <td className={styles.mono}>{a.from}</td>
                  <td className={styles.mono}>{a.bill}</td>
                  <td>{inr(a.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {p.count > 500 && (
        <a className={styles.linkBtn} href={fileUrl(run.id, c.planFile)}>
          Download the full plan ({p.count} rows)
        </a>
      )}
    </>
  );
}

function Matcher({ kind, runs, activeRunId, onChanged }) {
  const c = COPY[kind];
  const mine = useMemo(() => runs.filter((r) => r.kind === kind), [runs, kind]);
  const [selected, setSelected] = useState(null);
  const [starting, setStarting] = useState(false);
  const current = selected || (mine[0] && mine[0].id);

  const start = async () => {
    setStarting(true);
    try {
      const data = await zb(`/matching/${kind}/preview`, { method: "POST" });
      setSelected(data.runId);
      onChanged();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.runHead}>
        <div>
          <div className={styles.cardTitle}>{c.title}</div>
          <div className={styles.muted}>{c.what}</div>
        </div>
        <Button className={styles.primaryBtn} variant="contained" disabled={starting || Boolean(activeRunId)} onClick={start}>
          {starting ? <CircularProgress size={16} color="inherit" /> : "Preview plan"}
        </Button>
      </div>
      <RunPicker runs={mine} current={current} onSelect={setSelected} activeRunId={activeRunId} />
      {current && (
        <RunPanel
          key={current}
          runId={current}
          onChanged={onChanged}
          post={(run) => ({
            label: "Apply in Zoho",
            title: `Apply ${run.preview.count} ${kind === "credits" ? "credit" : "payment"} application(s)?`,
            body: (
              <p>
                This applies <strong>{inr(run.preview.total)}</strong> to Ingram bills in Zoho Books, exactly as previewed.
                If anything in Zoho changed since the preview, nothing is applied and you will be asked to preview again.
              </p>
            ),
          })}
        >
          {(run) => <PlanBody run={run} kind={kind} />}
        </RunPanel>
      )}
    </div>
  );
}

export default function MatchingTab(props) {
  return (
    <>
      <div className={styles.infoBox}>
        These steps look at <strong>every</strong> outstanding Ingram bill in Zoho Books, not just the latest statement.
        Post the month’s bills and credit notes first, then apply credits, then payments.
      </div>
      <Matcher kind="credits" {...props} />
      <Matcher kind="payments" {...props} />
    </>
  );
}
